import { t } from "@lingui/macro";

import {
  CloseIconSmall,
  CouponCodeIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

type Props = {
  appliedCode: string;
  calculationType?: string | null;
  discountAmount?: number | null;
  discountType?: string | null;
  errorMessage: string | null;
  isApplyingPromoCode: boolean;
  isEditable: boolean;
  onRemove: () => void;
};

const getAppliedPromoCodeSubtitle = ({
  calculationType,
  currencySymbol,
  discountAmount,
  discountType,
}: {
  calculationType?: string | null;
  currencySymbol: string;
  discountAmount?: number | null;
  discountType?: string | null;
}) => {
  let formattedDiscount: string;

  switch (discountType) {
    case "PERCENTAGE":
      formattedDiscount = t`${discountAmount ?? 0}% off`;
      break;
    case "FIXED":
      formattedDiscount = t`${currencySymbol}${ConvertCentsToDollars(
        discountAmount ?? 0
      )} off`;
      break;
    default:
      return t`Discount applied`;
  }

  switch (calculationType) {
    case "SINGLE_ROUND":
    case "SINGLE_ITEM":
      return t`${formattedDiscount} from one item`;
    case "BOOKING":
      return t`${formattedDiscount} from all items`;
    default:
      return t`Discount applied`;
  }
};

export const AppliedPromoCodeState = ({
  appliedCode,
  calculationType,
  discountAmount,
  discountType,
  errorMessage,
  isApplyingPromoCode,
  isEditable,
  onRemove,
}: Props) => {
  const { currencySymbol } = useGetClubCurrency();

  return (
    <VStack gap={3} testID="booking-promo-code">
      <HStack
        alignItems="center"
        gap={3}
        backgroundColor="surface-primary"
        borderRadius="1"
        paddingHorizontal={3}
        paddingVertical={1}
        borderWidth={4}
        borderColor="surface-secondary"
      >
        <Box
          width={32}
          height={20}
          borderRadius="0.5"
          backgroundColor="surface-secondary"
          alignItems="center"
          justifyContent="center"
        >
          <CouponCodeIcon color="icon-primary" width={16} height={16} />
        </Box>
        <VStack flex={1}>
          <Text variant="mainSmallSemiBold" color="text-primary">
            {appliedCode}
          </Text>
          <Text variant="mainTiny" color="text-secondary">
            {getAppliedPromoCodeSubtitle({
              calculationType,
              currencySymbol,
              discountAmount,
              discountType,
            })}
          </Text>
        </VStack>
        {isEditable ? (
          <Pressable
            accessibilityLabel={t`Remove promo code`}
            accessibilityRole="button"
            backgroundColor="surface-status-error-subtlest"
            borderRadius="1"
            padding={2}
            disabled={isApplyingPromoCode}
            onPress={onRemove}
          >
            <CloseIconSmall color="icon-status-error" width={16} height={16} />
          </Pressable>
        ) : null}
      </HStack>
      {errorMessage ? (
        <Text variant="mainTiny" color="text-error" testID="coupon-code-error">
          {errorMessage}
        </Text>
      ) : null}
    </VStack>
  );
};
